import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "src/shared/hooks/useForm";
import { selectMainState, useDispatch, useSelector } from "src/stateManagement/redux";
import { AddRequirementsInLocation } from "src/stateManagement/redux/slices/location/asyncReducers";
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { normalizeText } from "src/shared/util/normalizeText";
import Swal from "sweetalert2";
import { StatusConditions, TypesOfActivities } from "src/stateManagement/redux/slices/shared/interfaces";
import { IUpdateDiagnosticForActivity } from "src/shared/util/updateDataServer";
import { getTranslationsForTable } from "src/shared/traslations";
import { tableStyles } from "src/shared/util/tableStyles";
import WarningIcon from '@mui/icons-material/Warning';
import Typography from "@mui/material/Typography";

interface Props {
    open: boolean;
    onClose: () => void;
    originFieldActivityId: string;
    activeDiagnostic?: IUpdateDiagnosticForActivity;
    addRemoveOptimizationActivity: (activity: any, add: boolean) => void;
    onActivityCreated: (activityData: { types: string[], simulationDate?: string, isWellWithMultiplesTDSEntries: boolean, existingActivities?: any[] }) => void;
}

const getModalStyle = (showCreateForm: boolean) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: showCreateForm ? 500 : 900,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    //border: "2px solid #000",
    boxShadow: 24,
    gap: 2,
    p: 4,
    overflow: "auto",
});

export const QuickAddOptimizationModal = ({
    open,
    onClose,
    originFieldActivityId,
    activeDiagnostic,
    addRemoveOptimizationActivity,
    onActivityCreated,
}: Props) => {
    const dispatch = useDispatch();
    const { locationId, projectId, locationName, lastStatusConditions, optimizationRequirements: currentRequirements } = useSelector((state: any) => state.location);
    const { configModules } = useSelector(selectMainState);
    const { activeUser } = useSelector((state: any) => state.main);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [typesActivities, setTypesActivities] = useState<TypesOfActivities[]>([]);
    const [statusConditions, setStatusConditions] = useState<StatusConditions[]>([]);
    const [optimizationActivitiesStates, setOptimizationActivitiesStates] = useState<any[]>([]);
    const [listActiveBloquers, setlistActiveBloquers] = useState<any>([]);
    const [enableOptions, setEnableOptions] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [dataToTable, setDataToTable] = useState<any[]>([]);

    const [formValues, handleInputChange, reset, setNuewValues] = useForm({
        type: "",
        comments: "",
        status: "pending",
        isWellWithMultiplesTDSEntries: false,
        simulationDate: undefined,
        targetProduction: undefined,
        lastProduction: undefined,
        requiredDate: moment().format("YYYY-MM-DD"),
        requiredUserId: activeUser?.id || "",
        locationId: locationId,
        projectId: projectId,
        typeActivity: "Optimization",
        statusActivity: "pending",
        originFieldActivityId: originFieldActivityId,
    });

    const { type, comments, status, simulationDate, targetProduction, lastProduction, isWellWithMultiplesTDSEntries, requiredUserId } = formValues;

    // Configurar columnas de la tabla
    useEffect(() => {
        const optimizationActivitiesSelected = activeDiagnostic?.optimizationActivitiesSelected || [];

        setColumns([
            {
                field: "activity",
                headerName: "Tipo de Actividad",
                minWidth: 150,
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "requiredDate",
                headerName: "Fecha Solicitado",
                minWidth: 100,
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "lastProduction",
                headerName: "Última medida Aceptada",
                minWidth: 100,
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "status",
                headerName: "Estado",
                minWidth: 100,
                flex: 1,
                headerAlign: "center",
                align: "center",
            },
            {
                field: "simulated",
                headerName: "Simulado",
                minWidth: 50,
                flex: 0.5,
                type: "boolean",
                headerAlign: "center",
                align: "center",
            },
            {
                field: "isWellWithMultiplesTDSEntries",
                headerName: "Repetitivo",
                headerAlign: "center",
                align: "center",
                minWidth: 50,
                flex: 0.5,
                renderCell: (params) => {
                    return params.row.isWellWithMultiplesTDSEntries ? (
                        <Grid
                            sx={{
                                display: "flex",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}
                        >
                            <WarningIcon color="warning" fontSize="small" />
                            <Typography color="warning">Si</Typography>
                        </Grid>
                    ) : (
                        <Grid
                            sx={{
                                display: "flex",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}
                        >
                            <Typography color="text.secondary">No</Typography>
                        </Grid>
                    );
                },
            },
            {
                field: "actions",
                headerName: "",
                headerAlign: "center",
                align: "center",
                width: 120,
                renderCell: (params) => {
                    const id = `${params?.row?.optimizationActivityId}-${params?.row?.activity}`;
                    const optimizationActivitiesSelected = activeDiagnostic?.optimizationActivitiesSelected || [];
                    const selected = optimizationActivitiesSelected.find(
                        (item) => item.id === id
                    );

                    return (
                        <Grid
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                color={selected ? "error" : "primary"}
                                onClick={() =>
                                    addRemoveOptimizationActivity(
                                        {...params.row, typeActivity: "Optimization"},
                                        !selected
                                    )
                                }
                            >
                                {selected ? (
                                    <DeleteForeverIcon fontSize="medium" />
                                ) : (
                                    <AddCircleOutlineIcon fontSize="medium" />
                                )}
                            </IconButton>
                        </Grid>
                    );
                },
            },
        ]);
    }, [activeDiagnostic, addRemoveOptimizationActivity]);

    // Preparar datos para la tabla
    useEffect(() => {
        const requirementsToTable = currentRequirements.map((item: any) => {
            return {
                ...item,
                simulated: item.simulationDate ? true : false,
            };
        });
        setDataToTable(requirementsToTable);
    }, [currentRequirements]);

    useEffect(() => {
        if (Array.isArray(configModules?.typesActivitiesForOptimization)) {
            setTypesActivities(configModules?.typesActivitiesForOptimization);
        }
        if (Array.isArray(configModules?.statusConditions)) {
            setStatusConditions(configModules?.statusConditions);
        }
        if (Array.isArray(configModules?.optimizationActivitiesStates)) {
            const filtedStates = configModules?.optimizationActivitiesStates?.filter(
                (state: any) => state?.type?.includes("Requirement")
            );
            setOptimizationActivitiesStates(filtedStates);
        }
    }, [configModules]);

    useEffect(() => {
        const blockers = [];
        const types = type.split(',').map((item: string) => item.trim()).filter(Boolean);

        if (types.length === 0) {
            setlistActiveBloquers([]);
            return;
        }

        for (const typeOfAction of types) {
            const typeActivity = typesActivities.find(
                (activity: any) => activity.label === typeOfAction
            );
            const values: any = { ...lastStatusConditions };

            for (const blocker of typeActivity?.deppendOf || []) {
                const statusBlocker = statusConditions.find(
                    (status: any) => status.field === blocker
                )?.warningStatus;

                if (statusBlocker?.includes(values[blocker])) {
                    blockers.push(blocker);
                }
            }
        }

        setlistActiveBloquers(blockers);
    }, [formValues, typesActivities, statusConditions, lastStatusConditions]);

    useEffect(() => {
        const enableOptionsFiltered = [];

        for (const activity of typesActivities) {
            const exist = currentRequirements.find(
                (requirement: any) => requirement.variableId === activity.id && requirement.status === "pending"
            );
            if (!exist) {
                enableOptionsFiltered.push(activity.label);
            }
        }
        setEnableOptions(enableOptionsFiltered);
    }, [typesActivities, currentRequirements]);

    const handleClose = () => {
        reset();
        setErrors([]);
        setShowCreateForm(false);
        onClose();
    };

    const rows = dataToTable.map((item: any) => {
        return {
            variableId: item.variableId,
            optimizationActivityId: item.optimizationActivityId,
            activity: item.activity,
            blocker: item.blocker,
            comments: item.comments,
            executedDate: moment(item.executedDate).format("DD-MM-YYYY"),
            originFieldActivityExecutedDate: moment(
                item.originFieldActivityExecutedDate
            ).format("DD-MM-YYYY"),
            originFieldActivityId: item.originFieldActivityId,
            requiredBy: item.requiredBy,
            requiredById: item.requiredById,
            requiredDate: moment(item.requiredDate).format("DD-MM-YYYY"),
            simulated: item.simulated,
            status: item.status,
            isWellWithMultiplesTDSEntries: item.isWellWithMultiplesTDSEntries || false,
        };
    });

    const handleSubmit = () => {
        const errors = [];
        const messageWithErrors = [];

        // Obtener el originFieldActivityId del prop o del formValues
        const finalOriginFieldActivityId = originFieldActivityId || formValues.originFieldActivityId;

        if (!type || type.trim() === "") {
            errors.push("type");
            messageWithErrors.push("El campo Tipo de Actividad es obligatorio");
        }

        if (!requiredUserId) {
            errors.push("requiredUserId");
            messageWithErrors.push("El campo requerido por es obligatorio");
        }

        // Validar que originFieldActivityId no esté vacío
        if (!finalOriginFieldActivityId || finalOriginFieldActivityId.trim() === "") {
            errors.push("originFieldActivityId");
            messageWithErrors.push("El campo Actividad asociada es obligatorio");
        }

        setErrors(errors);
        if (errors.length > 0) {
            Swal.fire({
                title: "Error",
                html: `<p>No se puede agregar debido a los siguientes errores:</p><ul>${messageWithErrors.map(error => `<li>${error}</li>`).join('')}</ul>`,
                icon: "error",
                confirmButtonText: "Aceptar",
            });
            return;
        }

        const blocker = listActiveBloquers?.map((blocker: any) => {
            const values: any = { ...lastStatusConditions };
            const statusBlocker = statusConditions.find(
                (status: any) => status.field === blocker
            );
            return { [String(statusBlocker?.name)]: values[blocker] };
        });

        const types = type.split(',').map((item: string) => item.trim()).filter(Boolean);

        // Verificar si alguna de las actividades ya existe en optimizationRequirements
        // con un estado distinto a 'completed' o 'discarded'
        const existingActivities: any[] = [];
        const newActivities: string[] = [];

        for (const typeOfAction of types) {
            const element = typesActivities.find(
                (activity: any) => normalizeText(activity.label) === normalizeText(typeOfAction)
            );

            if (!element?.id) {
                continue;
            }

            // Buscar si existe una actividad con el mismo variableId y estado válido
            const existingActivity = currentRequirements.find((req: any) => 
                req.variableId === element.id && 
                req.status !== 'completed' && 
                req.status !== 'discarded'
            );

            if (existingActivity) {
                existingActivities.push({
                    optimizationActivityId: existingActivity.optimizationActivityId,
                    activity: typeOfAction,
                    simulated: !!existingActivity.simulationDate,
                    isWellWithMultiplesTDSEntries: existingActivity.isWellWithMultiplesTDSEntries || false,
                });
            } else {
                newActivities.push(typeOfAction);
            }
        }

        // Si hay actividades existentes, prepararlas para agregar al diagnóstico
        if (existingActivities.length > 0 && newActivities.length === 0) {
            // Todas las actividades ya existen, solo agregarlas al diagnóstico
            onActivityCreated({
                types: [],
                simulationDate: undefined,
                isWellWithMultiplesTDSEntries: false,
                existingActivities: existingActivities,
            });

            Swal.fire({
                title: "Éxito",
                text: existingActivities.length === 1 
                    ? "La opción de optimización ya existe y ha sido agregada al diagnóstico"
                    : `${existingActivities.length} opciones de optimización ya existen y han sido agregadas al diagnóstico`,
                icon: "success",
                confirmButtonText: "Aceptar",
            });
            handleClose();
            return;
        }

        // Si hay actividades nuevas, crearlas
        if (newActivities.length > 0) {
            const requirements = [];

            for (const typeOfAction of newActivities) {
                const element = typesActivities.find(
                    (activity: any) => normalizeText(activity.label) === normalizeText(typeOfAction)
                );

                if (!element?.id) {
                    continue;
                }

                requirements.push({
                    id: element.id,
                    location: locationName,
                    name: typeOfAction,
                    value: "Si",
                    type: "Optimization",
                    comments: comments || "",
                    blocker: blocker || [],
                    status,
                    simulationDate,
                    targetProduction,
                    lastProduction,
                    isWellWithMultiplesTDSEntries,
                } as any);
            }

            // Construir el objeto data con solo los campos requeridos en el nivel superior
            // La estructura debe coincidir con la curl que funciona correctamente
            const data: any = {
                requiredDate: formValues.requiredDate,
                requiredUserId: formValues.requiredUserId,
                locationId: formValues.locationId,
                projectId: formValues.projectId,
                type: "Optimization",
                status: "pending",
                originFieldActivityId: finalOriginFieldActivityId,
                requirements,
            };

            dispatch(AddRequirementsInLocation(data));
            
            // Mensaje según si se agregaron existentes y nuevas
            let message = "";
            if (existingActivities.length > 0) {
                message = `${existingActivities.length} opción(es) existente(s) agregada(s) y ${newActivities.length} nueva(s) creada(s)`;
            } else {
                message = "Opción de optimización creada exitosamente";
            }

            Swal.fire({
                title: "Éxito",
                text: message,
                icon: "success",
                confirmButtonText: "Aceptar",
            });

            // Pasar los datos necesarios para que el componente padre busque y seleccione las actividades nuevas
            // También incluir las actividades existentes si las hay
            onActivityCreated({
                types: newActivities,
                simulationDate,
                isWellWithMultiplesTDSEntries,
                existingActivities: existingActivities.length > 0 ? existingActivities : undefined,
            });
        }

        setShowCreateForm(false);
        reset();
    };

    const paginationModel = { page: 0, pageSize: 10 };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={getModalStyle(showCreateForm)}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                backgroundColor: "#cf1919",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#ff3535",
                                },
                                width: 30,
                                height: 30,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: "1rem", ml: 3, fontWeight: "bold" }}>
                            {showCreateForm ? "Crear Nueva Opción de Optimización" : "Opciones de Optimización"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {!showCreateForm && (
                            <IconButton
                                onClick={() => setShowCreateForm(true)}
                                sx={{
                                    backgroundColor: "#03365e",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#129ED3",
                                    },
                                    width: 30,
                                    height: 30,
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {!showCreateForm ? (
                    // Vista de lista
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <DataGrid
                                rows={getTranslationsForTable([...rows], "es", [
                                    "status",
                                ])}
                                columns={columns}
                                initialState={{ pagination: { paginationModel } }}
                                pageSizeOptions={[10, 20, 50, 100]}
                                checkboxSelection={false}
                                sx={{
                                    mt: 2,
                                    height: 400,
                                    ...tableStyles
                                }}
                                getRowId={(row) =>
                                    row.optimizationActivityId + row.variableId
                                }
                            />
                        </Grid>
                    </Grid>
                ) : (
                    // Vista de formulario
                    <>
                        <Grid sx={{ padding: 1 }}>
                            <InputLabel id="demo-simple-select-label">
                                Tipo de Actividad
                            </InputLabel>
                            <Autocomplete
                                multiple
                                id="size-small-standard-multi"
                                size="small"
                                options={enableOptions}
                                value={type !== "" ? type?.split(',').map((item: string) => item.trim()).filter(Boolean) : []}
                                onChange={(event: any, newValue: any) => {
                                    setNuewValues({
                                        ...formValues,
                                        type: newValue.join(',')
                                    });
                                }}
                                sx={{
                                    borderRadius: "4px",
                                    padding: "1px 1px",
                                    border: errors.includes("type") ? "1px solid red" : "none",
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        error={errors.includes("type")}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={{ padding: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="demo-simple-select-label">
                                    Estado
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    label="Estado"
                                    id="demo-simple-select"
                                    name="status"
                                    value={status}
                                    onChange={handleInputChange}
                                    size="small"
                                >
                                    {optimizationActivitiesStates.map((state: any, index) => {
                                        return (
                                            <MenuItem value={state.id} key={index}>
                                                {state.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid
                            sx={{
                                display: "flex",
                                padding: "5px 0px",
                                border: "1px solid rgba(0, 0, 0, 0.23)",
                                borderRadius: "4px",
                                marginRight: 1,
                                marginLeft: 1
                            }}
                        >
                            <FormControl fullWidth>
                                <FormControlLabel
                                    label={"Pozo repetitivo:"}
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            checked={isWellWithMultiplesTDSEntries}
                                            onChange={(event) => handleInputChange({
                                                target: {
                                                    name: "isWellWithMultiplesTDSEntries",
                                                    value: event.target.checked,
                                                }
                                            })}
                                        />
                                    }
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid sx={{ padding: 1, display: "flex", alignItems: "center" }}>
                            <DesktopDatePicker
                                defaultValue={null}
                                label="Fecha Simulación"
                                name="simulationDate"
                                value={simulationDate ? moment(simulationDate) : null}
                                onChange={(event) => {
                                    const formattedDate = moment(event).format("YYYY-MM-DD");
                                    handleInputChange({
                                        target: {
                                            name: "simulationDate",
                                            value: formattedDate,
                                        }
                                    });
                                }}
                                sx={{
                                    width: "100%",
                                }}
                            />
                            <IconButton
                                size="small"
                                color="warning"
                                disabled={!simulationDate}
                                onClick={() => {
                                    handleInputChange({
                                        target: {
                                            name: "simulationDate",
                                            value: null,
                                        }
                                    });
                                }}
                            >
                                <RemoveCircleIcon fontSize="medium" />
                            </IconButton>
                        </Grid>
                        <Grid sx={{ padding: 1 }}>
                            <TextField
                                fullWidth
                                size="medium"
                                name="targetProduction"
                                label="Última Medida (BBPD)"
                                value={targetProduction || 0}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                    handleInputChange({
                                        target: {
                                            name: "targetProduction",
                                            value: numericValue,
                                        }
                                    });
                                }}
                            />
                        </Grid>
                        <Grid sx={{ padding: 1 }}>
                            <TextField
                                fullWidth
                                size="medium"
                                name="lastProduction"
                                label="Ganancia Estimada (BBPD)"
                                value={lastProduction || 0}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                    handleInputChange({
                                        target: {
                                            name: "lastProduction",
                                            value: numericValue,
                                        }
                                    });
                                }}
                            />
                        </Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Comentario"
                            multiline
                            rows={4}
                            sx={{ mt: 2 }}
                            value={comments}
                            name="comments"
                            onChange={handleInputChange}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={handleSubmit}
                        >
                            AGREGAR
                        </Button>
                    </>
                )}
            </Box>
        </Modal>
    );
};
